/**
 * @file Builds the copy-paste code snippet that exchanges a token's secret
 * for a session, for the Console API or the Backstage API.
 */
const AUDIENCE_BACKSTAGE = "api-backstage";

const COMMENT_SECRET = `// The secret of this token. The platform gives it one time, therefore keep
// it in the environment of your client.`;

const COMMENT_CONSOLE_AUTHORIZE = `// Step 2: name the console organization, and read the organizations of the
//         operator.`;

type Props = {
  host: string;
  consoleOrganizationId?: string;
  isConsoleOperator: boolean;
};

type StepProps = {
  host: string;
  consoleOrganizationId?: string;
};

/**
 * Builds the snippet that exchanges the token secret for an authentication JWT.
 */
const getAuthenticateSnippet = (host: string): string => {
  const snippet = `const HOST = "${host}";

${COMMENT_SECRET}
const secretToken = process.env.FUNDAMENTAL_ACCESS_TOKEN;

// Step 1: change the secret into the authentication token.
const authenticateResponse = await fetch(
  \`\${HOST}/api/identity/AuthenticateByPersonalAccessToken\`,
  {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      audience: "${AUDIENCE_BACKSTAGE}",
      secretToken,
    }),
  },
);

const { data: identity } = await authenticateResponse.json();
const { authenticationJwt } = identity;
`;

  return snippet;
};

/**
 * Builds the snippet that opens a console organization session from the token.
 */
const getConsoleSnippet = ({
  host,
  consoleOrganizationId,
}: StepProps): string => {
  const authenticateSnippet = getAuthenticateSnippet(host);

  const snippet = `${authenticateSnippet}
${COMMENT_CONSOLE_AUTHORIZE}
const consoleResponse = await fetch(
  \`\${HOST}/api/backstage/Authorize?organizationId=${consoleOrganizationId}\`,
  {
    method: "PATCH",
    headers: { authentication: authenticationJwt },
  },
);

const { data: consoleUser } = await consoleResponse.json();
const { organizations } = consoleUser;

// Step 3: open the session of one organization of that list. One
//         authorization token opens one organization only.
const [ firstOrganization ] = organizations;

const authorizeResponse = await fetch(
  \`\${HOST}/api/backstage/Authorize?organizationId=\${firstOrganization.id}\`,
  {
    method: "PATCH",
    headers: { authentication: authenticationJwt },
  },
);

const { data: user } = await authorizeResponse.json();
const { authorizationJwt } = user;

// Step 4: put the authorization token into the authorization header.
const response = await fetch(\`\${HOST}/api/backstage/ListProjects\`, {
  method: "GET",
  headers: { authorization: authorizationJwt },
});
`;

  return snippet;
};

/**
 * Builds the snippet that opens a Backstage organization session from the token.
 */
const getBackstageSnippet = ({ host }: StepProps): string => {
  const authenticateSnippet = getAuthenticateSnippet(host);

  const snippet = `${authenticateSnippet}
// Step 2: change the authentication token into the authorization token.
const authorizeResponse = await fetch(\`\${HOST}/api/backstage/Authorize\`, {
  method: "PATCH",
  headers: { authentication: authenticationJwt },
});

const { data: user } = await authorizeResponse.json();
const { authorizationJwt } = user;

// Step 3: put the authorization token into the authorization header.
const response = await fetch(\`\${HOST}/api/backstage/IndexOptions\`, {
  method: "GET",
  headers: { authorization: authorizationJwt },
});
`;

  return snippet;
};

// NOTE: the token carries the access of its owner, therefore the client
//       makes the same two exchanges that the browser makes. A console
//       operator does the second exchange two times, because the first call
//       gives the organizations and the second call opens one of them.
/**
 * Builds the exchange snippet for the operator's console or Backstage access.
 */
const getAccessTokenSnippet = ({
  host,
  consoleOrganizationId,
  isConsoleOperator,
}: Props): string => {
  if (isConsoleOperator) {
    const consoleSnippet = getConsoleSnippet({
      host,
      consoleOrganizationId,
    });

    return consoleSnippet;
  }

  const backstageSnippet = getBackstageSnippet({ host });

  return backstageSnippet;
};

export default getAccessTokenSnippet;
